import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {DataTable} from 'primereact/datatable'
import {Dropdown} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import {Message} from 'primereact/message'
import Logo from '../../assets/icons/TaVIE-Logo.svg'

import {Sidebar} from 'primereact/sidebar'
import {Tooltip} from 'primereact/tooltip'
import {forwardRef, useImperativeHandle, useMemo} from 'react'
import {HTTP_STATUS_OK, SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {isValidDate} from '../../utils/DateTimeUtils'
import {useAuth} from '../authentication/AuthProvider'
import {useListHospitals} from '../hospital/query-keys/HospitalQueryManager'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {importPatients} from '../table-utils/importForm/ImportEndPoint'
import {
    importErrorType,
    patientColumnHeaderReferenceList,
    valueType,
} from './patient/PatientTableConstants'

const ReviewImportDataTable = forwardRef(
    (
        {
            isAfterImport = false,
            handleOnChangeImportData = () => {},
            handleOnChangeHeader = () => {},
            handleOnDeleteRow = () => {},
        },
        ref,
    ) => {
        useSignals()
        useImperativeHandle(ref, () => ({
            showForm: handleShowForm,
            hideForm: handleHideForm,
        }))

        const {user} = useAuth()
        const {currentHospital} = useUserHospital()
        const {userRole} = useAppConfig()
        const {translateCommons} = TranslateUtils()
        const visible = useSignal(false)
        const hospitalSelectImport = useSignal(null)
        const importData = useSignal([])
        const errorList = useSignal([])
        const headerList = useSignal([])

        const missingColumns = useMemo(() => {
            if (!errorList.value?.length) {
                return []
            }

            return errorList.value.map(item => {
                if (item?.errorType === importErrorType.missingMinimalColumns) {
                    return item?.field
                }
            })
        }, [errorList.value])

        const headerErrorList = useMemo(() => {
            if (!errorList.value || errorList.value?.length < 1) {
                return []
            }

            return errorList.value
                ?.filter(
                    error =>
                        error.errorType === importErrorType.notAcceptedColumn,
                )
                .map(item => item?.field)
        }, [errorList.value])

        const valueErrorList = useMemo(() => {
            if (!errorList.value || errorList.value?.length < 1) {
                return []
            }

            return errorList.value?.filter(
                error => error.errorType === importErrorType.notAcceptedValues,
            )
        }, [errorList.value])

        const {data: hospitalOptions} = useListHospitals(
            user?.id,
            true,
            {},
            !!user?.id && !!isAfterImport && !!visible.value,
        )

        const handleShowForm = value => {
            if (!!value) {
                importData.value = value?.importData
                errorList.value = value?.errorList
                headerList.value = value?.headerList
            }

            visible.value = true
        }

        const handleHideForm = () => {
            visible.value = false
        }

        const textEditor = options => {
            return (
                <InputText
                    type="text"
                    value={options.value}
                    onChange={e => options.editorCallback(e.target.value)}
                />
            )
        }

        const resolveErrorImport = () => {
            let errorHolderList = []
            for (let i = 0; i < headerList.value.length; i++) {
                if (headerList.value[i]) {
                    for (let j = 0; j < importData.value?.length; j++) {
                        const rowTemp = importData.value[j]

                        const columnHeaderRefItem =
                            patientColumnHeaderReferenceList.find(
                                item =>
                                    item?.column === headerList.value[i]?.field,
                            )

                        if (!!columnHeaderRefItem) {
                            if (
                                columnHeaderRefItem.valueType === valueType.enum
                            ) {
                                if (
                                    !!rowTemp[columnHeaderRefItem?.column] &&
                                    !columnHeaderRefItem.acceptedValues.includes(
                                        rowTemp[columnHeaderRefItem?.column],
                                    )
                                ) {
                                    errorHolderList.push({
                                        field: headerList.value[i]?.field,
                                        rowIndex: j,
                                        errorType:
                                            importErrorType.notAcceptedValues,
                                    })
                                }
                            } else if (
                                columnHeaderRefItem.valueType === valueType.date
                            ) {
                                if (
                                    !isValidDate(
                                        rowTemp[columnHeaderRefItem?.column],
                                    )
                                ) {
                                    errorHolderList.push({
                                        field: headerList.value[i]?.field,
                                        rowIndex: j,
                                        errorType:
                                            importErrorType.notAcceptedValues,
                                    })
                                }
                            }
                        } else {
                            if (
                                !errorHolderList.some(
                                    error =>
                                        error?.field ===
                                        headerList.value[i]?.field,
                                )
                            ) {
                                errorHolderList.push({
                                    field: headerList.value[i]?.field,
                                    errorType:
                                        importErrorType.notAcceptedColumn,
                                })
                            }
                        }
                    }
                }
            }

            errorList.value = errorHolderList
        }

        const onCellEditComplete = e => {
            let {rowData, newValue, field} = e

            if (!!newValue && newValue.trim().length > 0)
                rowData[field] = newValue
            else rowData[field] = ''

            // Resolve Error Import Data List and Call Back
            if (handleOnChangeImportData) {
                resolveErrorImport()

                handleOnChangeImportData({
                    importData: importData.value,
                    errorList: errorList.value,
                })
            }
        }

        const handleRemoveHeaderColumn = headerField => {
            if (handleOnChangeHeader) {
                handleOnChangeHeader(headerField)
            }

            headerList.value = headerList.value.filter(
                header => header?.field !== headerField,
            )

            resolveErrorImport()

            handleOnChangeImportData({
                importData: importData.value,
                errorList: errorList.value,
            })
        }

        const handleImportFile = async () => {
            if (
                userRole === SystemRole.ADMIN &&
                isAfterImport &&
                !hospitalSelectImport.value
            ) {
                showNotification(
                    severityNotification.warn,
                    'Please select hospital to import patients !!',
                )
                return
            }

            let toPostData =
                importData.value?.length > 0 ? importData.value : []

            if (toPostData.length > 0) {
                const response = await importPatients(
                    user?.id,
                    userRole === SystemRole.USER_WITH_ROLE
                        ? currentHospital?.id
                        : hospitalSelectImport.value,
                    toPostData,
                )
                if (response === HTTP_STATUS_OK) {
                    importData.value = []

                    handleHideForm()
                }
            } else {
                showNotification(
                    severityNotification.warn,
                    'Import data cannot be empty !!',
                )
            }
        }

        const hospitalItemTemplate = option => {
            return (
                <div className="flex align-items-center">
                    <img
                        alt={option.name}
                        src={
                            option?.extraData && option.extraData?.icon
                                ? option.extraData?.icon
                                : Logo
                        }
                        className={`mr-2`}
                        style={{width: '18px'}}
                    />
                    <div>{option.label}</div>
                </div>
            )
        }

        const handleDeleteRow = uid => {
            if (!uid) {
                return
            }

            if (!importData.value || !importData.value?.length) {
                return
            }

            importData.value = importData.value?.filter(
                data => data?.uid !== uid,
            )

            if (handleOnDeleteRow) {
                handleOnDeleteRow(uid)
            }
        }

        return (
            <Sidebar
                id="sidebar-advanced-filter"
                className="sidebar-filter"
                visible={visible.value}
                onHide={handleHideForm}
                showCloseIcon={true}
                blockScroll
                position="right"
                baseZIndex={130}>
                <div className="py-2 px-1">
                    {/* Header */}
                    <div className="flex align-items-center">
                        <div className="mr-2">
                            <i
                                className="pi pi-table"
                                style={{fontSize: '26px'}}
                            />
                        </div>
                        <div>
                            <h3 className="font-bold m-0">Review Datatable</h3>
                        </div>
                    </div>
                    <div>
                        <p>
                            A table displays the data from the recently imported
                            file, allowing you to review and edit any mismatched
                            columns before finalizing the import to ensure the
                            accuracy of the data.
                        </p>
                    </div>
                    <hr />
                    {/* Content */}

                    {userRole === SystemRole.ADMIN && isAfterImport && (
                        <div className="w-full mb-3">
                            {!hospitalSelectImport.value && (
                                <Message
                                    className="w-full mb-2"
                                    severity="error"
                                    icon="pi pi-exclamation-triangle"
                                    text="Please choose the hospital you want to import patients for."
                                />
                            )}
                            <h4 className="font-bold">Select Hospital</h4>
                            <Dropdown
                                className="w-full font-bold"
                                options={hospitalOptions}
                                value={hospitalSelectImport.value}
                                optionLabel="label"
                                optionValue="value"
                                placeholder={translateCommons(
                                    'Select hospital for import patients',
                                )}
                                itemTemplate={hospitalItemTemplate}
                                onChange={e =>
                                    (hospitalSelectImport.value = e.value)
                                }
                                filter
                                filterBy="label"
                                filterPlaceholder={translateCommons(
                                    'Search here',
                                )}
                                emptyMessage={translateCommons(
                                    'No records found',
                                )}
                                showFilterClear
                                resetFilterOnHide
                                showClear
                                emptyFilterMessage={translateCommons(
                                    'No results found',
                                )}
                                style={{
                                    '--inputColor': 'rgba(68, 184, 166, 0.1)',
                                }}
                            />
                        </div>
                    )}

                    {/* TODO: Use New Table */}
                    <div className="w-full overflow-hidden">
                        <Message
                            className="w-full mb-3"
                            severity="info"
                            icon="pi pi-info-circle"
                            text="Click on the cell of the table where the
                                    data needs to be edited."
                        />

                        {missingColumns?.length > 0 && (
                            <Message
                                className="w-full mb-3"
                                severity="error"
                                icon="pi pi-exclamation-triangle"
                                text="This dataset is missing the minimum required columns to create a patient record. Please ensure all the necessary columns are added based on the dataset we have provided."
                            />
                        )}
                        <DataTable
                            header={'Resolve Import Data Table'}
                            value={importData.value}
                            editMode="cell"
                            className="w-full"
                            size="small"
                            columnResizeMode="fit"
                            showGridlines
                            responsiveLayout="scroll">
                            <Column
                                field="action"
                                header={
                                    <>
                                        <div className="flex justify-content-center align-items-center">
                                            Action
                                            <i className="pi pi-cog ml-2"></i>
                                        </div>
                                    </>
                                }
                                body={rowData => {
                                    return (
                                        <div className="font-bold flex justify-content-center">
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-text p-button-danger"
                                                tooltip="Delete row"
                                                tooltipOptions={{
                                                    position: 'top',
                                                }}
                                                onClick={() =>
                                                    handleDeleteRow(
                                                        rowData?.uid,
                                                    )
                                                }
                                            />
                                        </div>
                                    )
                                }}></Column>
                            {headerList.value &&
                                headerList.value?.length > 0 &&
                                headerList.value?.map(item => {
                                    return (
                                        <Column
                                            field={item?.field}
                                            body={(rowData, row) => {
                                                let errorField

                                                if (
                                                    valueErrorList &&
                                                    valueErrorList?.length > 0
                                                ) {
                                                    errorField =
                                                        valueErrorList.find(
                                                            error =>
                                                                error?.field ===
                                                                    item?.field &&
                                                                error?.rowIndex ===
                                                                    row?.rowIndex,
                                                        )
                                                }

                                                return (
                                                    <div className="w-full cursor-pointer flex align-items-center">
                                                        {!!errorField && (
                                                            <i
                                                                className={`font-bold cursor-pointer p-2 surface-300 border-circle pi pi-exclamation-triangle col-${row?.rowIndex}-${item?.field} mr-2`}
                                                                style={{
                                                                    color: 'red',
                                                                }}>
                                                                <Tooltip
                                                                    style={{
                                                                        zIndex: '999999',
                                                                    }}
                                                                    target={`.col-${row?.rowIndex}-${item?.field}`}
                                                                    position="top"
                                                                    content={`${
                                                                        errorField &&
                                                                        errorField?.message
                                                                            ? errorField?.message
                                                                            : 'The cell value in the table is not part of the Accepted Values specified in the Column Header Reference.'
                                                                    }`}
                                                                />
                                                            </i>
                                                        )}
                                                        {rowData &&
                                                        rowData[item?.field]
                                                            ? rowData[
                                                                  item?.field
                                                              ]
                                                            : '-'}
                                                    </div>
                                                )
                                            }}
                                            key={item?.field}
                                            editor={options =>
                                                textEditor(options)
                                            }
                                            onCellEditComplete={
                                                onCellEditComplete
                                            }
                                            header={() => {
                                                return (
                                                    <div className="w-full flex align-items-center">
                                                        {headerErrorList &&
                                                            headerErrorList?.length >
                                                                0 &&
                                                            headerErrorList.includes(
                                                                item?.field,
                                                            ) && (
                                                                <i
                                                                    className={`font-bold cursor-pointer p-2 surface-300 border-circle pi pi-exclamation-triangle header-${item?.field} mr-2`}
                                                                    style={{
                                                                        color: 'red',
                                                                    }}>
                                                                    <Tooltip
                                                                        style={{
                                                                            zIndex: '999999',
                                                                        }}
                                                                        target={`.header-${item?.field}`}
                                                                        position="top"
                                                                        content="This header column do not match any columns in the Column Header References."
                                                                    />
                                                                </i>
                                                            )}
                                                        <span className="mr-2">
                                                            {item?.field}
                                                        </span>

                                                        <div
                                                            className="p-3 cursor-pointer border-circle hover:surface-100"
                                                            onClick={() =>
                                                                handleRemoveHeaderColumn(
                                                                    item?.field,
                                                                )
                                                            }>
                                                            <i
                                                                style={{
                                                                    color: 'red',
                                                                }}
                                                                className="font-bold pi pi-times"></i>
                                                        </div>
                                                    </div>
                                                )
                                            }}></Column>
                                    )
                                })}
                        </DataTable>
                    </div>

                    {/* Footer */}
                    <div className="my-2">
                        {isAfterImport && (
                            <Button
                                label={'Import'.toUpperCase()}
                                icon="pi pi-file-import"
                                className="col-12 p-button-lg font-bold mb-2"
                                onClick={handleImportFile}
                            />
                        )}
                        <Button
                            label={'Back'.toUpperCase()}
                            icon="pi pi-times"
                            className="col-12 p-button-lg p-button-outlined font-bold mb-2"
                            onClick={handleHideForm}
                        />
                    </div>
                </div>
            </Sidebar>
        )
    },
)

export default ReviewImportDataTable
